import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { CACHE_TIME } from '@hygge/shared';
import type { TaskFilters, TaskLinkType, BoardSettings } from '@hygge/shared';
import {
  getTasks,
  getTask,
  createTask,
  updateTask,
  deleteTask,
  reorderTasks,
  getStatuses,
  createStatus,
  updateStatus,
  deleteStatus,
  reorderStatuses,
  getBoardTaskStats,
  getBoardTags,
  createTag,
  updateTag,
  deleteTag,
  getBoardSettings,
  updateBoardSettings,
  createTaskLink,
  deleteTaskLink,
  setTaskParent,
  searchTasks,
  getComments,
  getTaskActivity,
  createComment,
  updateComment,
  deleteComment,
} from '@/lib/tasks';
import { bulkTaskAction, type BulkTaskPayload } from '@/lib/bulk-tasks';
import { subscribeTask, unsubscribeTask } from '@/lib/task-subscription';

export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (projectId: string, filters?: TaskFilters) => [...taskKeys.lists(), projectId, filters] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (key: string) => [...taskKeys.details(), key] as const,
};

export const statusKeys = {
  all: ['task-statuses'] as const,
  list: (projectId: string) => [...statusKeys.all, projectId] as const,
};

export const taskStatsKeys = {
  all: ['task-stats'] as const,
  project: (projectId: string) => [...taskStatsKeys.all, projectId] as const,
};

export const tagKeys = {
  all: ['task-tags'] as const,
  list: (projectId: string) => [...tagKeys.all, projectId] as const,
};

export const commentKeys = {
  all: ['task-comments'] as const,
  list: (taskKey: string) => [...commentKeys.all, taskKey] as const,
};

// ─── Tasks ──────────────────────────────────────────────────────────────

/**
 * One page of a board's tasks, plus the server's `meta` — which carries the
 * board's real total, not the size of this page.
 *
 * `TASKS_PAGE_LIMIT` is the server's own `MAX_LIMIT`, so a single request can
 * never return more; on a bigger board the response is a truncated slice and
 * `meta.total` is the only honest count. Callers rendering a count must read
 * `meta.total` and say plainly when the loaded set is smaller.
 */
export const TASKS_PAGE_LIMIT = 100;

export function useTasks(projectId: string | undefined, filters?: TaskFilters & { page?: number; limit?: number; sort?: string; order?: string }) {
  return useQuery({
    queryKey: taskKeys.list(projectId ?? '', filters),
    // Honour a caller-supplied limit instead of overwriting it — the previous
    // hardcode made the `limit` in the signature a lie.
    queryFn: () => getTasks(projectId!, { ...filters, limit: filters?.limit ?? TASKS_PAGE_LIMIT }),
    enabled: !!projectId,
    staleTime: CACHE_TIME.SHORT,
  });
}

export function useTask(key: string | undefined) {
  return useQuery({
    queryKey: taskKeys.detail(key ?? ''),
    queryFn: () => getTask(key!),
    enabled: !!key,
    staleTime: CACHE_TIME.SHORT,
  });
}

export function useCreateTask(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof createTask>[1]) =>
      createTask(projectId!, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
      qc.invalidateQueries({ queryKey: ['backlog'] });
      qc.invalidateQueries({ queryKey: ['board-overview', projectId ?? ''] });
      if (variables.sprintId) {
        qc.invalidateQueries({ queryKey: ['sprints'] });
      }
    },
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ key, ...payload }: { key: string } & Record<string, unknown>) =>
      updateTask(key, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
      qc.invalidateQueries({ queryKey: taskKeys.detail(variables.key) });
      qc.invalidateQueries({ queryKey: ['sprints'] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => deleteTask(key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
      qc.invalidateQueries({ queryKey: ['sprints'] });
    },
  });
}

export function useReorderTasks() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: reorderTasks,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
      qc.invalidateQueries({ queryKey: ['sprints'] });
    },
  });
}

export function useBulkTaskAction(boardId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: BulkTaskPayload) => bulkTaskAction(boardId!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
      qc.invalidateQueries({ queryKey: ['board-overview', boardId ?? ''] });
      // Sprint + backlog groupings can change when tasks move/assign/delete.
      qc.invalidateQueries({ queryKey: ['sprints'] });
      qc.invalidateQueries({ queryKey: ['backlog'] });
    },
  });
}

// ─── Subscriptions (follow / unfollow) ────────────────────────────────────

export function useSubscribeTask(taskKey: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => subscribeTask(taskKey!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskKey ?? '') });
    },
  });
}

export function useUnsubscribeTask(taskKey: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => unsubscribeTask(taskKey!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskKey ?? '') });
    },
  });
}

// ─── Statuses ───────────────────────────────────────────────────────────

export function useStatuses(projectId: string | undefined) {
  return useQuery({
    queryKey: statusKeys.list(projectId ?? ''),
    queryFn: () => getStatuses(projectId!),
    enabled: !!projectId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useProjectTaskStats(projectId: string | undefined) {
  return useQuery({
    queryKey: taskStatsKeys.project(projectId ?? ''),
    queryFn: () => getBoardTaskStats(projectId!),
    enabled: !!projectId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * Task counts for several boards at once.
 *
 * Keyed identically to {@link useProjectTaskStats}, so a board already opened
 * costs nothing here and opening one afterwards costs nothing either — a
 * different key would silently double every request the board pages make.
 *
 * Request ceiling: one per board. Correct at ~10 boards, linear thereafter.
 * Above roughly 40 this wants a `GET /boards/summary` aggregate, which does not
 * exist and is deliberately not a blocker.
 */
export function useBoardsTaskStats(boardIds: string[]) {
  return useQueries({
    queries: boardIds.map((id) => ({
      queryKey: taskStatsKeys.project(id),
      queryFn: () => getBoardTaskStats(id),
      staleTime: CACHE_TIME.DEFAULT,
    })),
  });
}

export function useProjectTags(projectId: string | undefined) {
  return useQuery({
    queryKey: tagKeys.list(projectId ?? ''),
    queryFn: () => getBoardTags(projectId!),
    enabled: !!projectId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useCreateTag(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: { name: string; color: string }) =>
      createTag(projectId!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tagKeys.list(projectId ?? '') });
    },
  });
}

export function useUpdateTag(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; name?: string; color?: string }) =>
      updateTag(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tagKeys.list(projectId ?? '') });
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
}

export function useDeleteTag(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteTag(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tagKeys.list(projectId ?? '') });
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
}

export function useCreateStatus(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof createStatus>[1]) =>
      createStatus(projectId!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: statusKeys.list(projectId ?? '') });
    },
  });
}

export function useUpdateStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Parameters<typeof updateStatus>[1]) =>
      updateStatus(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: statusKeys.all });
    },
  });
}

export function useDeleteStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, migrateToStatusId }: { id: string; migrateToStatusId?: string }) =>
      deleteStatus(id, migrateToStatusId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: statusKeys.all });
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
}

export function useReorderStatuses(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (items: Array<{ id: string; sortOrder: number }>) =>
      reorderStatuses(projectId!, items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: statusKeys.list(projectId ?? '') });
    },
  });
}

// ─── Board Settings ─────────────────────────────────────────────────────

export const boardSettingsKeys = {
  all: ['board-settings'] as const,
  project: (projectId: string) => [...boardSettingsKeys.all, projectId] as const,
};

export function useBoardSettings(projectId: string | undefined) {
  return useQuery({
    queryKey: boardSettingsKeys.project(projectId ?? ''),
    queryFn: () => getBoardSettings(projectId!),
    enabled: !!projectId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useUpdateBoardSettings(projectId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: Partial<BoardSettings>) =>
      updateBoardSettings(projectId!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boardSettingsKeys.project(projectId ?? '') });
    },
  });
}

// ─── Links ──────────────────────────────────────────────────────────────

export function useCreateTaskLink(taskKey: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: { targetKey: string; linkType: TaskLinkType }) =>
      createTaskLink(taskKey!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskKey ?? '') });
    },
  });
}

export function useDeleteTaskLink(taskKey: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (linkId: string) => deleteTaskLink(linkId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskKey ?? '') });
    },
  });
}

// ─── Subtasks / Parent ──────────────────────────────────────────────────

export function useSetTaskParent(taskKey: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (parentKey: string | null) => setTaskParent(taskKey!, parentKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskKey ?? '') });
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
}

// ─── Search ─────────────────────────────────────────────────────────────

export function useSearchTasks(projectId: string | undefined, query: string, excludeKey?: string) {
  return useQuery({
    queryKey: ['task-search', projectId, query, excludeKey],
    queryFn: () => searchTasks(projectId!, query, excludeKey),
    enabled: !!projectId && query.length >= 1,
    staleTime: CACHE_TIME.SHORT,
  });
}

// ─── Activity history ─────────────────────────────────────────────────────

export function useTaskActivity(key: string | undefined) {
  return useQuery({
    queryKey: [...taskKeys.detail(key ?? ''), 'activity'],
    queryFn: () => getTaskActivity(key!),
    enabled: !!key,
    staleTime: CACHE_TIME.SHORT,
  });
}

// ─── Comments ───────────────────────────────────────────────────────────

export function useTaskComments(taskKey: string | undefined) {
  return useQuery({
    queryKey: commentKeys.list(taskKey ?? ''),
    queryFn: () => getComments(taskKey!),
    enabled: !!taskKey,
    staleTime: CACHE_TIME.SHORT,
  });
}

export function useCreateComment(taskKey: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: { body: string; parentId?: string; source?: string }) =>
      createComment(taskKey!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commentKeys.list(taskKey ?? '') });
      qc.invalidateQueries({ queryKey: taskKeys.details() });
    },
  });
}

export function useUpdateComment(taskKey: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      updateComment(commentId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commentKeys.list(taskKey ?? '') });
    },
  });
}

export function useDeleteComment(taskKey: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) => deleteComment(commentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commentKeys.list(taskKey ?? '') });
      qc.invalidateQueries({ queryKey: taskKeys.details() });
    },
  });
}
